import { useFlags } from "@/utils/flagsmith/useFlags";
import { DetailTableRow } from "../../components/DetailTable";
import { scrollAndHighlightRow } from "../../utils/scrollAndHighlight";
import { useColorizedValue } from "../useColorizedValue";
import {
    FinanceLabField,
    FinanceLabFieldType,
    formatFinanceLabValue,
} from "@/entities/FinanceLab";
import { getLast12Months } from "@/utils/metrics/metricsUtils";
import { COMING_SOON } from "@/entities/CreditBox/CreditBox.types";
import { useAtomValue } from "jotai";
import { postClosingMetricsAtom } from "../../metrics.state";

export function usePostClosingMetricsData(): DetailTableRow[] {
    const flags = useFlags();
    const showComingSoonFields = flags.enabled("show-coming-soon-placeholders");

    const metricsProjectionsMetrics = useAtomValue(postClosingMetricsAtom);

    const totalAssetsToEquityMetric =
        metricsProjectionsMetrics[
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS_TO_EQUITY
        ];

    const interestCoverageMetric =
        metricsProjectionsMetrics[FinanceLabField.DEAL_INTEREST_COVERAGE];

    const dscrMetric = metricsProjectionsMetrics[FinanceLabField.DEAL_DSCR];

    const grossDebtToAssetsMetric =
        metricsProjectionsMetrics[FinanceLabField.DEAL_GROSS_DEBT_TO_ASSETS];

    const debtToEquityMetric =
        metricsProjectionsMetrics[FinanceLabField.DEAL_DEBT_TO_EQUITY];

    const grossDebtToEbitdaMetric =
        metricsProjectionsMetrics[FinanceLabField.DEAL_GROSS_DEBT_TO_EBITDA];

    const cashBurnMonthlyMetric =
        metricsProjectionsMetrics[
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_CASH_BURN_MONTHLY
        ];

    const interestCoverageColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(interestCoverageMetric?.values),
            FinanceLabFieldType.PERCENTAGE,
        ) || "-",
        interestCoverageMetric?.source,
        interestCoverageMetric?.info.derived.label,
    );

    const dscrColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(dscrMetric?.values),
            FinanceLabFieldType.PERCENTAGE,
        ) || "-",
        dscrMetric?.source,
        dscrMetric?.info.derived.label,
    );

    const debtToAssetsColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(grossDebtToAssetsMetric?.values),
            FinanceLabFieldType.MULTIPLE,
        ) || "-",
        grossDebtToAssetsMetric?.source,
        grossDebtToAssetsMetric?.info.derived.label,
    );

    const debtToEquityColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(debtToEquityMetric?.values),
            FinanceLabFieldType.MULTIPLE,
        ) || "-",
        debtToEquityMetric?.source,
        debtToEquityMetric?.info.derived.label,
    );

    const debtToEbitdaColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(grossDebtToEbitdaMetric?.values),
            FinanceLabFieldType.MULTIPLE,
        ) || "-",
        grossDebtToEbitdaMetric?.source,
        grossDebtToEbitdaMetric?.info.derived.label,
    );

    const assetToEquityColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(totalAssetsToEquityMetric?.values),
            FinanceLabFieldType.MULTIPLE,
        ) || "-",
        totalAssetsToEquityMetric?.source,
        totalAssetsToEquityMetric?.info.derived.label,
    );

    const monthlyCashBurnColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(cashBurnMonthlyMetric?.values),
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        cashBurnMonthlyMetric?.source,
        cashBurnMonthlyMetric?.info.derived.label,
    );

    return [
        {
            label: "Interest Coverage Ratio",
            value: interestCoverageColorized.value,
            valueClassName: interestCoverageColorized.className,
            source: interestCoverageColorized.source,
            sourceClassName: interestCoverageColorized.sourceClassName,
            delta: interestCoverageMetric?.delta,
            onClick: () =>
                scrollAndHighlightRow(
                    "summary-post-closing-metrics",
                    "Interest Coverage Ratio",
                ),
        },
        {
            label: "Debt Service Coverage Ratio",
            value: dscrColorized.value,
            valueClassName: dscrColorized.className,
            source: dscrColorized.source,
            sourceClassName: dscrColorized.sourceClassName,
            delta: dscrMetric?.delta,
            onClick: () =>
                scrollAndHighlightRow(
                    "summary-post-closing-metrics",
                    "Debt Service Coverage Ratio",
                ),
        },
        {
            label: "Debt-to-Assets Ratio",
            value: debtToAssetsColorized.value,
            valueClassName: debtToAssetsColorized.className,
            source: debtToAssetsColorized.source,
            sourceClassName: debtToAssetsColorized.sourceClassName,
            delta: grossDebtToAssetsMetric?.delta,
            onClick: () =>
                scrollAndHighlightRow(
                    "summary-post-closing-metrics",
                    "Debt-to-Assets Ratio",
                ),
        },
        {
            label: "Debt-to-Equity Ratio",
            value: debtToEquityColorized.value,
            valueClassName: debtToEquityColorized.className,
            source: debtToEquityColorized.source,
            sourceClassName: debtToEquityColorized.sourceClassName,
            delta: debtToEquityMetric?.delta,
            onClick: () =>
                scrollAndHighlightRow(
                    "summary-post-closing-metrics",
                    "Debt-to-Equity Ratio",
                ),
        },
        {
            label: "Debt-to-EBITDA Ratio",
            value: debtToEbitdaColorized.value,
            valueClassName: debtToEbitdaColorized.className,
            source: debtToEbitdaColorized.source,
            sourceClassName: debtToEbitdaColorized.sourceClassName,
            delta: grossDebtToEbitdaMetric?.delta,
            onClick: () =>
                scrollAndHighlightRow(
                    "summary-post-closing-metrics",
                    "Debt-to-EBITDA Ratio",
                ),
        },
        {
            label: "Asset-to-Equity",
            value: assetToEquityColorized.value,
            valueClassName: assetToEquityColorized.className,
            source: assetToEquityColorized.source,
            sourceClassName: assetToEquityColorized.sourceClassName,
            delta: totalAssetsToEquityMetric?.delta,
            onClick: () =>
                scrollAndHighlightRow(
                    "summary-post-closing-metrics",
                    "Asset-to-Equity",
                ),
        },
        ...(showComingSoonFields
            ? [
                  {
                      label: "Fixed Charge Ratio",
                      value: COMING_SOON,
                  },
              ]
            : []),
        {
            label: "Monthly cash burn",
            value: monthlyCashBurnColorized.value,
            valueClassName: monthlyCashBurnColorized.className,
            source: monthlyCashBurnColorized.source,
            sourceClassName: monthlyCashBurnColorized.sourceClassName,
            delta: cashBurnMonthlyMetric?.delta,
            onClick: () =>
                scrollAndHighlightRow(
                    "summary-post-closing-metrics",
                    "Monthly cash burn",
                ),
        },
    ];
}
